// =============================================================================
// ARTWORK TYPES
// =============================================================================

/**
 * The two types of artworks supported:
 * - "imagetarget": Animation content overlaid on a tracked image (AR marker)
 * - "worldtarget": 3D scene panels placed in world space (no image tracking)
 */
export type ArtworkType = "imagetarget" | "worldtarget";

// =============================================================================
// CREATOR API TYPES (raw data from Creator CMS)
// =============================================================================

export interface CreatorFile {
  type: string;
  path?: string;
  width?: number;
  height?: number;
  size?: number;
  checksum?: string;
  fileID?: string;
}

export interface CreatorArtworkImages {
  animation?: CreatorFile;
  artwork1024?: CreatorFile;
  artwork512?: CreatorFile;
  qrCode?: CreatorFile;
  Image?: CreatorFile;
}

export interface CreatorUserData {
  userid?: string;
  webarEnabled?: boolean;
}

/**
 * A single artwork from Creator CMS.
 * Can be either an ImageTarget (animation on tracked image) or WorldTarget (3D panel scene).
 */
export interface CreatorArtwork {
  partitionkey: string;
  id?: string;
  type?: "artwork";
  sortkey?: "Artwork";
  name?: string;
  description?: string;

  // Artwork type determination (use getArtworkType() helper for consistent detection)
  artworkType?: ArtworkType;
  isWorldTarget?: boolean;

  // ImageTarget fields (animation content)
  animationHasAlpha?: boolean;
  animationVideoOrientation?: string;

  // WorldTarget fields (panel/scene content)
  sceneConfig?: string;

  // Asset files
  files?: CreatorFile[];
  images?: CreatorArtworkImages;

  // Metadata
  checksum?: string;
  cover?: string;
  qrcode?: string;
  constructedCover?: string;
  constructedQR?: string;
  data?: string;
  created?: string;
  modified?: string;
  status?: "published" | "draft";
  publishEnabled?: boolean;
  enabled?: boolean;
  downloadsCount?: number;
  viewsCount?: number;
  viewsSeconds?: number;
  creatorVersion?: string;
  class?: string | null;
  version?: number;
  userData?: CreatorUserData;
}
  
// =============================================================================
// COLLECTION TYPE
// =============================================================================

/**
 * A collection of artworks from Creator CMS.
 * Contains multiple artworks that can be either ImageTarget or WorldTarget types.
 */
export interface CreatorCollection {
  artworks: CreatorArtwork[];
  type?: "collection";
  id?: string;
  partitionkey?: string;
  sortkey?: string;
  name?: string;
  title?: string;
  description?: string;

  // Assets
  images?: CreatorArtworkImages;
  cover?: string;
  constructedCover?: string;
  qrcode?: string;
  constructedQR?: string;

  // Metadata
  checksum?: string;
  created?: string;
  modified?: string;
  data?: string;
  status?: string;
  publishEnabled?: boolean;
  publishedCount?: number;
  downloadsCount?: number;
  viewsCount?: number;
  webarEnabled?: boolean;
  relationships?: { Item: CreatorArtwork }[];
  class?: string | null;
  version?: number;
  userData?: CreatorUserData;
}

// =============================================================================
// MICROSITE / OUTPUT TYPES
// =============================================================================

/** Metadata extracted from a collection for microsite display */
export interface MicrositeData {
  name: string;
  description: string;
  constructedCover: string;
}

/**
 * Input payload from Creator CMS - can be either a single artwork or a collection.
 * Use isCollection() helper to determine type.
 */
export type CreatorPayload = CreatorCollection | CreatorArtwork;

// =============================================================================
// TYPE GUARDS & HELPERS
// =============================================================================

/** Check if payload is a collection (has artworks array) */
export const isCollection = (payload: CreatorPayload): payload is CreatorCollection =>
  "artworks" in payload && Array.isArray(payload.artworks);

/** Check if payload is a single artwork */
export const isArtwork = (payload: CreatorPayload): payload is CreatorArtwork =>
  payload.type === "artwork" || !isCollection(payload);

/**
 * Get the artwork type, normalizing various formats from the API.
 * Returns "worldtarget" for panel/scene content, "imagetarget" for AR marker content.
 */
export const getArtworkType = (artwork: CreatorArtwork): ArtworkType => {
  const explicitType = artwork.artworkType?.trim().toLowerCase() as ArtworkType | undefined;
  if (explicitType === "worldtarget" || explicitType === "imagetarget") {
    return explicitType;
  }
  if (artwork.isWorldTarget) return "worldtarget";
  return "imagetarget";
};

/** Extract artworks array from payload (handles both collection and single artwork) */
export const getArtworks = (payload: CreatorPayload): CreatorArtwork[] => {
  if (isCollection(payload)) return payload.artworks;
  if (payload.type === "artwork") return [payload];
  return [];
};

// =============================================================================
// PANEL / SCENECONFIG TYPES (WorldTarget layout data)
// =============================================================================

/**
 * File entry in a panel's sceneConfig layout.
 * More strict than CreatorFile - requires dimensions for 3D positioning.
 */
export interface PanelFile {
  path: string;
  width: number;
  height: number;
  fileID: string | null;
  /** Asset type: Image, VideoAlpha, ImageCover (ignored), etc. */
  type?: string;
  size?: number;
  [key: string]: unknown;
}

/** Minimal panel artwork entry - files + sceneConfig JSON. */
export interface PanelArtworkEntry {
  files?: PanelFile[];
  sceneConfig?: string;
  name?: string;
  id?: string;
  [key: string]: unknown;
}

export interface SceneConfigAsset {
  assetID: string;
  assetType: string;
  assetPath: string;
}

export interface SceneConfigChild {
  sceneID: string;
  assetID: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
}

export interface SceneConfigScene {
  sceneID: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  children?: SceneConfigChild[];
}

/** Parsed sceneConfig JSON structure for WorldTarget panel layouts */
export interface SceneConfig {
  version: number;
  assets: SceneConfigAsset[];
  scenes: SceneConfigScene[];
}